<!--
 * @Description: 营销管理/活动运营报表/定向发放活动报表
-->
<template>
  <scroll-layout>
    <template #header>
      <ykc-search
        ref="YkcSearch"
        :data="searchItems"
        :searchForm="searchParams"
        @click="handleSearchConfirm"
        @clean="handleSearchClean" />
    </template>
    <div>
      <!-- 汇总数据区 -->
      <div class="summary-cards">
        <div class="summary-card">
          <div class="summary-card__inner summary-card__inner--blue">
            <div class="summary-card__icon">
              <i class="el-icon-user-solid"></i>
            </div>
            <div class="summary-card__info">
              <p class="summary-card__label">全量活动实际参与用户总数</p>
              <p class="summary-card__value">{{ totalUsers || 0 | numberFormat }}</p>
            </div>
          </div>
          <div class="summary-card__tip">
            <i class="el-icon-info"></i>
            数据统计时间范围：{{ queryTimeRange }}
          </div>
        </div>
        <div class="summary-card">
          <div class="summary-card__inner summary-card__inner--green">
            <div class="summary-card__icon">
              <i class="el-icon-coin"></i>
            </div>
            <div class="summary-card__info">
              <p class="summary-card__label">全量活动实际发放卡券总数</p>
              <p class="summary-card__value">{{ totalIssueCount || 0 | numberFormat }}</p>
            </div>
          </div>
        </div>
        <div class="summary-card">
          <div class="summary-card__inner summary-card__inner--amber">
            <div class="summary-card__icon">
              <i class="el-icon-ticket"></i>
            </div>
            <div class="summary-card__info">
              <p class="summary-card__label">全量活动实际核销卡券总数</p>
              <p class="summary-card__value">{{ totalVerifyCount || 0 | numberFormat }}</p>
            </div>
          </div>
        </div>
      </div>

      <ykc-table
        ref="YkcTable"
        :data="tableData"
        :title="tableTitle"
        operateFixedType="right"
        :columns="tableColumns"
        :operateButtons="tableOperateButtons.filter(item => item.enabled())">
        <template #headerButtons>
          <div class="table-header-button">
            <ykc-button
              type="plain"
              v-rbac="'marketing:activityReport:directed:export'"
              @click="exportBlob">
              导出
            </ykc-button>
          </div>
        </template>
        <ykc-pagination
          slot="pagination"
          ref="YkcPagination"
          :total="total"
          :current-page.sync="currentPage"
          @current-change="handlePaginationCurrentChange"
          :page-size.sync="pageSize"
          @size-change="handlePaginationSizeChange" />
      </ykc-table>
    </div>
  </scroll-layout>
</template>

<script>
  import { offlineExport, getLastTime } from '@/utils';
  import { directedIssueReport } from '@/service/apis';

  export default {
    name: 'directedIssueReportList',
    data() {
      const startDate = getLastTime('yyyy-MM-dd', 30);
      const endDate = getLastTime('yyyy-MM-dd', 0);

      return {
        totalUsers: 0,
        totalIssueCount: 0,
        totalVerifyCount: 0,
        queryTimeRange: '',
        searchParams: {
          queryTime: [startDate, endDate],
          queryTimeStart: startDate,
          queryTimeEnd: endDate,
        },
        tableData: [],
        total: 0,
        currentPage: 1,
        pageSize: 10,
        termList: [
          {
            title: '活动编码',
            desc: '活动的唯一标识编码，用于系统内部识别和关联活动数据。',
          },
          {
            title: '活动名称',
            desc: '运营人员配置的活动展示名称，便于识别和管理。',
          },
          {
            title: '活动类型',
            desc: '活动的分类标签，本报表仅展示"定向发放"类型的活动数据。',
          },
          {
            title: '实际参与用户数',
            desc: '定向发放对象已领取卡券的用户数量，不包含重复领取的用户。',
          },
          {
            title: '实际发放卡券数量',
            desc: '实际发放给用户的卡券总数量，一个用户可能获得多张卡券。',
          },
          {
            title: '实际核销卡券数量',
            desc: '用户在充电消费时实际使用抵扣的卡券数量，反映活动的实际转化效果。',
          },
        ],
        termMap: {
          actualParticipantCount: '特定活动，特定时间段（查询时间段）内领取了活动卡券的用户数',
          actualIssueCount: '特定活动，特定时间段（查询时间段）内发放卡券总数量',
          actualVerifyCount:
            '特定活动，特定时间段（查询时间段）内发放的卡券截止当前时间已被使用的数量',
        },
        tableTitle: '活动数据列表',
        tableOperateButtons: [],
        tableColumns: [
          {
            label: '活动编码',
            prop: 'activityCode',
            minWidth: '180px',
          },
          {
            label: '活动名称',
            prop: 'activityName',
            minWidth: '200px',
          },
          {
            label: '活动类型',
            prop: 'activityType',
            minWidth: '100px',
            scopedSlots: {
              default: () => '定向发放',
            },
          },
          {
            label: '活动开始时间',
            prop: 'activityBeginTime',
            minWidth: '160px',
          },
          {
            label: '活动结束时间',
            prop: 'activityEndTime',
            minWidth: '160px',
          },
          {
            label: '实际参与用户数',
            prop: 'actualParticipantCount',
            minWidth: '140px',
            align: 'right',
            scopedSlots: {
              header: ({ column }) =>
                this.renderHeaderWithTip(column.label, 'actualParticipantCount'),
              default: ({ row }) => {
                return row.actualParticipantCount != null
                  ? Number(row.actualParticipantCount).toLocaleString()
                  : '——';
              },
            },
          },
          {
            label: '实际发放卡券数量',
            prop: 'actualIssueCount',
            minWidth: '160px',
            align: 'right',
            scopedSlots: {
              header: ({ column }) => this.renderHeaderWithTip(column.label, 'actualIssueCount'),
              default: ({ row }) => {
                return row.actualIssueCount != null
                  ? Number(row.actualIssueCount).toLocaleString()
                  : '——';
              },
            },
          },
          {
            label: '实际核销卡券数量',
            prop: 'actualVerifyCount',
            minWidth: '160px',
            align: 'right',
            scopedSlots: {
              header: ({ column }) => this.renderHeaderWithTip(column.label, 'actualVerifyCount'),
              default: ({ row }) => {
                return row.actualVerifyCount != null
                  ? Number(row.actualVerifyCount).toLocaleString()
                  : '——';
              },
            },
          },
        ],
      };
    },
    computed: {
      searchItems() {
        return [
          {
            comName: 'YkcInput',
            key: 'activityCode',
            label: '活动编码',
            placeholder: '请输入活动编码',
          },
          {
            comName: 'YkcInput',
            key: 'activityName',
            label: '活动名称',
            placeholder: '请输入活动名称',
          },
          {
            comName: 'YkcDatePicker',
            type: 'daterange',
            format: 'yyyy-MM-dd',
            clearable: true,
            key: 'activityBeginTime',
            label: '活动开始时间',
            limitDate: 'limitAfter',
          },
          {
            comName: 'YkcDatePicker',
            type: 'daterange',
            format: 'yyyy-MM-dd',
            key: 'queryTime',
            label: '数据查询时间',
            limitDate: 'limitAfter',
          },
        ];
      },
    },
    created() {
      this.getData();
    },
    methods: {
      renderHeaderWithTip(label, prop) {
        const tip = this.termMap[prop];
        if (!tip) return label;
        return (
          <span style="display: inline-flex; align-items: center; gap: 4px;">
            {label}
            <el-tooltip content={tip} placement="top">
              <i
                class="el-icon-question"
                style="color: #909399; cursor: pointer; font-size: 14px;"
              />
            </el-tooltip>
          </span>
        );
      },
      /**
       * 点击查询
       */
      handleSearchConfirm(form) {
        this.currentPage = 1;
        const search = JSON.parse(JSON.stringify(form));
        if (search.activityBeginTime && search.activityBeginTime.length === 2) {
          [search.activityBeginTimeStart, search.activityBeginTimeEnd] = search.activityBeginTime;
        }
        if (search.queryTime && search.queryTime.length === 2) {
          [search.queryTimeStart, search.queryTimeEnd] = search.queryTime;
        }
        this.searchParams = search || {};
        this.getData();
      },
      /**
       * 点击清空查询按钮
       */
      handleSearchClean(form) {
        Object.assign(this.searchParams, form);
        const startDate = getLastTime('yyyy-MM-dd', 30);
        const endDate = getLastTime('yyyy-MM-dd', 0);
        this.searchParams.queryTime = [startDate, endDate];
        this.searchParams.queryTimeStart = startDate;
        this.searchParams.queryTimeEnd = endDate;
      },
      /**
       * 导出
       */
      exportBlob() {
        offlineExport({
          downloadType: 'marketing_export',
          jsonNode: {
            downloadKey: 'activity_report_directedIssue',
            ...this.searchParams,
          },
        });
      },

      /**
       * 分页 change 事件监听
       */
      handlePaginationCurrentChange(current) {
        this.currentPage = current;
        this.getData();
      },
      /**
       * 分页 size change 事件监听
       */
      handlePaginationSizeChange(size) {
        this.currentPage = 1;
        this.pageSize = size;
        this.getData();
      },
      /**
       * 获取远程数据
       */
      getData() {
        const loading = this.$loading({
          lock: true,
          text: 'Loading',
          spinner: 'el-icon-loading',
          background: 'rgba(0, 0, 0, 0.7)',
        });
        const listReq = directedIssueReport.list({
          ...(this.searchParams || {}),
          current: this.currentPage,
          size: this.pageSize,
        });
        const summaryReq = directedIssueReport.summary({
          ...(this.searchParams || {}),
        });
        Promise.all([listReq, summaryReq])
          .then(([listRes, summaryRes]) => {
            this.tableData = listRes.records || [];
            this.total = listRes.total || 0;
            this.totalUsers = summaryRes.totalParticipants || 0;
            this.totalIssueCount = summaryRes.totalIssueCount || 0;
            this.totalVerifyCount = summaryRes.totalVerifyCount || 0;
            this.queryTimeRange = summaryRes.queryTimeRange || '';
          })
          .catch(err => {
            console.error('directedIssueReport getData error:', err);
          })
          .finally(() => {
            loading.close();
          });
      },
    },
  };
</script>

<style lang="scss" scoped>
  @import '@/components/ykc-ui/styles/var.scss';

  .summary-cards {
    display: flex;
    gap: 16px;
    margin-bottom: 16px;
  }

  .summary-card {
    flex: 1;
    background: #fff;
    border: 1px solid #ebebf0;
    border-radius: 8px;
    padding: 20px;
    display: flex;
    flex-direction: column;
    justify-content: space-between;

    &__inner {
      display: flex;
      align-items: center;
      gap: 16px;
      padding: 16px 20px;
      border-radius: 8px;
      border: 1px solid;

      &--blue {
        background: linear-gradient(to right, #eff6ff, #fff);
        border-color: #bfdbfe;
      }

      &--green {
        background: linear-gradient(to right, #ecfdf5, #fff);
        border-color: #a7f3d0;
      }

      &--amber {
        background: linear-gradient(to right, #fffbeb, #fff);
        border-color: #fde68a;
      }
    }

    &__icon {
      width: 48px;
      height: 48px;
      border-radius: 8px;
      display: flex;
      align-items: center;
      justify-content: center;

      .summary-card__inner--blue & {
        background: rgba(37, 99, 235, 0.1);

        i {
          font-size: 24px;
          color: #2563eb;
        }
      }

      .summary-card__inner--green & {
        background: rgba(16, 185, 129, 0.1);

        i {
          font-size: 24px;
          color: #10b981;
        }
      }

      .summary-card__inner--amber & {
        background: rgba(245, 158, 11, 0.1);

        i {
          font-size: 24px;
          color: #f59e0b;
        }
      }
    }

    &__info {
      display: flex;
      flex-direction: column;
    }

    &__label {
      font-size: 12px;
      color: #64748b;
      margin: 0 0 4px 0;
    }

    &__value {
      font-size: 24px;
      font-weight: 700;
      color: #1e293b;
      margin: 0;
    }

    &__tip {
      font-size: 12px;
      color: #64748b;
      margin-top: 8px;

      i {
        margin-right: 4px;
      }
    }
  }

  .term-list {
    .term-item {
      background: #f6f7f8;
      border-radius: 8px;
      padding: 16px;
      margin-bottom: 12px;

      &:last-child {
        margin-bottom: 0;
      }

      &__title {
        font-size: 14px;
        font-weight: 500;
        color: #1e293b;
        margin: 0 0 4px 0;
      }

      &__desc {
        font-size: 13px;
        color: #64748b;
        margin: 0;
      }
    }
  }
</style>
